import type { ChallengeKind } from '@sea-wars/protocol';
import { type SyntheticEvent, useEffect, useRef } from 'react';
import { useChallenges } from '../api/challenges.js';

const KIND_LABEL: Record<ChallengeKind, string> = {
  challenge: 'wyzywa Cię na pojedynek',
  rematch: 'chce zagrać rewanż',
};

const BUSY_LABEL: Record<ChallengeKind, string> = {
  challenge: 'nie może teraz odebrać Twojego wyzwania',
  rematch: 'nie może teraz odebrać Twojej propozycji rewanżu',
};

/**
 * Wyskakujący popup ponad interfejsem, widoczny z każdego ekranu (patrz `ChallengesProvider`)
 * — dla wyzwania NA POJEDYNEK i oferty rewanżu jednym i tym samym komponentem, po kolei jedno
 * naraz (nie lista jak dawny stały baner). „Później” zamyka popup bez żadnego wywołania API —
 * zaproszenie zostaje `pending` po stronie serwera, a znacznik w nagłówku (`App.tsx`) pozwala
 * do niego wrócić.
 */
export function NotificationPopup() {
  const { activeIncoming, busyNotice, respond, snooze, dismissBusyNotice } = useChallenges();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const open = Boolean(busyNotice || activeIncoming);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  if (!open) return null;

  // Esc zamyka <dialog> natywnie — bez przechwycenia stan Reacta by się nie zgadzał
  // (efekt wyżej próbowałby otworzyć popup ponownie przy następnym renderze).
  // Traktujemy to jak "Później"/"OK", nie jak anulowanie samej akcji.
  function handleCancel(event: SyntheticEvent<HTMLDialogElement>): void {
    event.preventDefault();
    if (busyNotice) dismissBusyNotice();
    else if (activeIncoming) snooze(activeIncoming.id);
  }

  return (
    <dialog ref={dialogRef} className="notice-dialog" onCancel={handleCancel}>
      {busyNotice ? (
        <>
          <h2 className="hand">Przeciwnik zajęty</h2>
          <p>
            <strong>{busyNotice.nickname}</strong> {BUSY_LABEL[busyNotice.kind]} — jest teraz w
            trakcie innego pojedynku.
          </p>
          <button type="button" className="btn btn--primary" onClick={dismissBusyNotice}>
            OK
          </button>
        </>
      ) : activeIncoming ? (
        <>
          <h2 className="hand">
            <strong>{activeIncoming.from.nickname}</strong> {KIND_LABEL[activeIncoming.kind]}
          </h2>
          <div className="notice-dialog__actions">
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => void respond(activeIncoming.id, true)}
            >
              Graj
            </button>
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => void respond(activeIncoming.id, false)}
            >
              Odrzuć
            </button>
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => snooze(activeIncoming.id)}
            >
              Później
            </button>
          </div>
        </>
      ) : null}
    </dialog>
  );
}
